import validator from 'validator';
import {identity} from '@packages/utilities/fp/utils';
import {getString} from '@packages/utilities/json';
import {Plugins, TypeImplementation, Result} from '@packages/systems/wfdl';
import {call1, _$, $, tyfun, func} from '@packages/utilities/wfdl-helpers';
import {
  TTypeConstructor,
  functionOf,
  TText,
  ECase,
  TUnion,
} from '@packages/systems/wfdl-ast';
import {
  qualify,
  typeName,
  pluginName,
  iconName,
  identifier,
} from '@packages/systems/wfdl-Qualified';
import {createSharedTextPluginValues} from '@packages/systems/plugins/Text';
import EmailIcon from './icons/Email';
import {
  makeRenderFunction,
  makeOptionalAlias,
} from '@packages/systems/plugins-utils/ValidatedTextInput';

export const TEmail = TTypeConstructor(
  qualify(Plugins.name('Email'), typeName('Email'))
);

export const TOptionalEmail = TTypeConstructor(
  qualify(Plugins.name('Email'), typeName('Optional Email'))
);

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    Email: TypeImplementation.Custom({
      parseValue: (json) => {
        const str = getString(json);
        if (str === undefined)
          return Result.Error('Expected a string for Email.');
        return validator.isEmail(str)
          ? Result.Ok(str)
          : Result.Error('Expected a valid Email literal.');
      },
      render: makeRenderFunction('Email'),
      icon: TypeImplementation.IconByName(
        qualify(pluginName('Email'), iconName('Email'))
      ),
    }),
    'Optional Email': makeOptionalAlias('Email'),
  },

  values: {
    ...createSharedTextPluginValues(TEmail, [
      'Equals',
      'Does not equal',
      'Contains',
      'Does not contain',
    ]),

    'Parse Email': {
      envType: {
        dataType: functionOf(
          TText,
          TUnion({
            Error: TText,
            Ok: TEmail,
          })
        ),
        exported: 'hidden',
      },
      // @ts-expect-error - TS7006 - Parameter 'inputText' implicitly has an 'any' type.
      value: (inputText) =>
        validator.isEmail(inputText)
          ? {tag: 'Ok', val: inputText}
          : {tag: 'Error', val: 'Must be a valid email.'},
    },

    'To Text': {
      envType: {
        dataType: functionOf(TEmail, TText),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      value: identity,
    },

    'Option Email to Text': {
      envType: {
        dataType: tyfun([TText, TOptionalEmail], TText),
        implicit: 'prefilledArgs',
        exported: 'visible',
      },
      expression: func(
        [identifier('fallback text'), identifier('optional email')],
        ECase({
          value: $('optional email'),
          cases: {
            Some: _$('Email', 'To Text'),
            None: func([identifier('_')], $('fallback text')),
          },
        })
      ),
    },
  },

  icons: {
    Email: EmailIcon,
  },
});

export const parseEmailRef = _$('Email', 'Parse Email');
export const parseEmail = call1(parseEmailRef);
